/**
 * Parse navigator.userAgent into a GDPR-safe simplified object.
 * Returns only: OS name/version, device type, browser name + major version.
 * Does NOT include raw UA string, screen resolution, or fingerprinting-capable data.
 *
 * @returns {{ os: string, device: string, browser: string }}
 */
const simplifyUserAgent = () => {
  const ua = navigator.userAgent || '';

  // Device type
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const device = isMobile ? 'mobile' : 'desktop';

  // OS detection
  let os = 'unknown';
  if (/Windows NT 10/.test(ua)) os = 'Windows 10+';
  else if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X\s*([\d_]+)?/.test(ua)) {
    const ver = ua.match(/Mac OS X\s*([\d_]+)?/);
    os = `macOS ${(ver?.[1] ?? '').replace(/_/g, '.')}`.trim();
  } else if (/CrOS/.test(ua)) os = 'ChromeOS';
  else if (/Android\s*([\d.]+)?/.test(ua)) {
    const ver = ua.match(/Android\s*([\d.]+)?/);
    os = `Android ${ver?.[1] ?? ''}`.trim();
  } else if (/iPhone OS\s*([\d_]+)?/.test(ua) || /iPad/.test(ua)) {
    const ver = ua.match(/(?:iPhone OS|CPU OS)\s*([\d_]+)?/);
    os = `iOS ${(ver?.[1] ?? '').replace(/_/g, '.')}`.trim();
  } else if (/Linux/.test(ua)) os = 'Linux';

  // Browser detection (order matters — check specific before generic)
  let browser = 'unknown';
  if (/EdgA?\/([\d]+)/.test(ua)) {
    browser = `Edge ${ua.match(/EdgA?\/([\d]+)/)[1]}`;
  } else if (/OPR\/([\d]+)/.test(ua)) {
    browser = `Opera ${ua.match(/OPR\/([\d]+)/)[1]}`;
  } else if (/SamsungBrowser\/([\d]+)/.test(ua)) {
    browser = `Samsung ${ua.match(/SamsungBrowser\/([\d]+)/)[1]}`;
  } else if (/CriOS\/([\d]+)/.test(ua)) {
    browser = `Chrome ${ua.match(/CriOS\/([\d]+)/)[1]}`;
  } else if (/FxiOS\/([\d]+)/.test(ua)) {
    browser = `Firefox ${ua.match(/FxiOS\/([\d]+)/)[1]}`;
  } else if (/Chrome\/([\d]+)/.test(ua) && !(/Edg/.test(ua))) {
    browser = `Chrome ${ua.match(/Chrome\/([\d]+)/)[1]}`;
  } else if (/Version\/([\d]+).*Safari/.test(ua)) { // NOSONAR client-side, bounded/trusted input
    browser = `Safari ${ua.match(/Version\/([\d]+)/)[1]}`;
  } else if (/Firefox\/([\d]+)/.test(ua)) {
    browser = `Firefox ${ua.match(/Firefox\/([\d]+)/)[1]}`;
  }

  return { os, device, browser };
};

export default simplifyUserAgent;
